import React, { useState, useEffect, useRef } from 'react';
import { Icon, IStyle, mergeStyles } from '@fluentui/react';
import { internal__useBwjScrollContainerStyles } from './styles';
import { BwjEventArgs } from '../../entities/bwj-event';
import { getVerticalOffsetFromAncestor } from '../../utils/dom';

interface IBwjScrollContainerProps {
  containerStyle?: IStyle;
  children?: React.ReactNode;
  invokeScrollReset?: BwjEventArgs<null> | undefined;/**argument is ignored */
  /**argument is the element to scroll to, and an offset.  Scrolling to the element alone puts the top of
   * the element at the top of the viewport.  Provide a positive offset if you want to position the element
   * of interest a bit from the top, or a negative offset to keep the top of the element out of view for whatever reason */
  invokeScrollTo?: BwjEventArgs<{selector: string, offset?: number}> | undefined;
  invokeScrollPositionReport?: BwjEventArgs<any> | undefined;/**argument is any object you want sent back in the onScrollChanged function invoked by this command */
  onScrollChanged?: (scrollTop: number, scrollLeft: number, containerWidth: number, containerHeight: number, scrollContainerSelector: string, context?: any) => void | undefined;
}

const SHOW_SCROLL_BUTTON_AFTER_TRAVELLING = 50;

export const BwjScrollContainer: React.FC<IBwjScrollContainerProps> = (props) => {
  const { invokeScrollReset, invokeScrollTo, invokeScrollPositionReport, onScrollChanged } = props;
  const [showVertical, setShowVertical] = useState(false);
  const [showHorizontal, setShowHorizontal] = useState(false);

  useEffect(() => {
    const e = containerRef.current;
    if(!!invokeScrollTo && invokeScrollTo.accessible() && !!e?.firstElementChild) {
      const args = invokeScrollTo.getArgs();
      const child = e.querySelector(args.selector);
      if(!child) { return; }
      // WARNING: THIS MIGHT BE WRONG.  REMOVE THIS COMMENT IF/WHEN THIS IS PROVEN TO WORK
      let offset = getVerticalOffsetFromAncestor(child as HTMLElement, e as HTMLElement);
      if(offset !== undefined) {
        offset += (args.offset ?? 0);
        scrollToVertical(offset);
      }
    }
  }, [invokeScrollTo]);

  useEffect(() => {
    if(!!invokeScrollReset && invokeScrollReset.accessible()) {
      invokeScrollReset.getArgs();
      scrollToTop();
      scrollLeft();
    }
  }, [invokeScrollReset]);

  useEffect(() => {
    const e = containerRef.current;
    if(!!invokeScrollPositionReport && invokeScrollPositionReport.accessible() && !!onScrollChanged && !!e) {
      onScrollChanged(e.scrollTop, e.scrollLeft, e.clientWidth, e.clientHeight, '#' + containerId.current, invokeScrollPositionReport.getArgs());
    }
  }, [invokeScrollPositionReport, onScrollChanged]);

  const styles = internal__useBwjScrollContainerStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const containerId = useRef(`bwj-scroll-${(new Date()).getTime()}`);
  const timeoutCancel = useRef<number | null>(null);

  const containerStyle: IStyle[] = [styles.defaultContainerStyle];
  !!props.containerStyle && containerStyle.push(props.containerStyle);

  const containerClass = mergeStyles(containerStyle);

  useEffect(() => {
    const onScrollChange = () => {
      timeoutCancel.current = null;
      const e = containerRef.current;
      if(!e) { return; }

      !!onScrollChanged && onScrollChanged(e.scrollTop, e.scrollLeft, e.clientWidth, e.clientHeight, '#' + containerId.current);
    };

    const handleScroll = () => {
      if (!containerRef.current) { return; }

      setShowVertical(containerRef.current.scrollTop > SHOW_SCROLL_BUTTON_AFTER_TRAVELLING);
      setShowHorizontal(containerRef.current.scrollLeft > SHOW_SCROLL_BUTTON_AFTER_TRAVELLING);

      if(!!timeoutCancel.current) {
        window.clearTimeout(timeoutCancel.current);
      }
      timeoutCancel.current = window.setTimeout(onScrollChange, 250);
    };

    // Attach the scroll event listener
    // window.addEventListener('scroll', handleScroll);
    containerRef.current?.addEventListener('scroll', handleScroll, true);
    // initialize scroll value for any listener
    timeoutCancel.current = window.setTimeout(onScrollChange, 250);

    // Clean up the event listener when the component is unmounted
    const containerRefValue = containerRef.current;
    return () => {
      if(!!timeoutCancel.current) {
        window.clearTimeout(timeoutCancel.current);
      }
      containerRefValue?.removeEventListener('scroll', handleScroll, true);
    };
  }, [onScrollChanged]);

  const scrollToVertical = (top: number) => {
    containerRef.current?.scrollTo({
      top,
      behavior: 'smooth',
    });
  };

  const scrollToTop = () => {
    containerRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const scrollLeft = () => {
    containerRef.current?.scrollTo({
      left: 0,
      behavior: 'smooth',
    });
  };

  const verticalButton = showVertical && <div role='button' className={styles.scrollButton} onClick={scrollToTop}><Icon iconName='ChevronUpMed' /></div>;
  const horizontalButton = showHorizontal && <div role='button' className={styles.scrollButton} onClick={scrollLeft}><Icon iconName='ChevronLeftMed' /></div>;

  return (
    <div id={containerId.current} className={containerClass} ref={containerRef}>
      <div className={styles.buttonPositioner}>
        <div className={styles.buttonContainer}>
          {verticalButton}
          {horizontalButton}
        </div>
      </div>
      {props.children}
    </div>
  );
};
